import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Heart } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { useCartStore } from "@/stores/cartStore";
import { Seo } from "@/components/Seo";
import { ProductImpactSection } from "@/components/shop/ProductImpactSection";
import { trackPurchase } from "@/lib/analytics";

const OrderConfirmation = () => {
  const [params] = useSearchParams();
  const orderId = params.get("order_id") ?? params.get("kco_order_id");
  // Kustom only populates {{session_id}} in the success URL, not order_id,
  // so we show the HPP session id as a customer reference when the real
  // order id isn't available yet.
  const hppSessionId = params.get("hpp_session_id");
  const referenceId = orderId ?? hppSessionId;
  const itemCount = useCartStore((s) => s.totalItems());
  const totalOre = useCartStore((s) => s.totalOre());
  const clearCart = useCartStore((s) => s.clearCart);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Track purchase med data fra cart-store FØR vi tømmer den.
    if (referenceId && itemCount > 0) {
      trackPurchase({ orderId: referenceId, totalOre, itemCount });
    }
    clearCart();
    // referenceId/itemCount/totalOre snapshottes på mount; vi vil ikke
    // re-track ved endring i state etter clearCart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearCart]);

  return (
    <Layout>
      <Seo
        title="Bestillingsbekreftelse – Håp Naturlab"
        description="Takk for bestillingen!"
        noIndex
      />
      <section className="mx-auto max-w-2xl px-6 py-20 text-center">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <h1 className="mt-6 text-3xl font-bold font-heading">Tusen takk for bestillingen!</h1>
        <p className="mt-4 text-lg text-muted-foreground font-body">
          Vi har mottatt bestillingen din, og du vil få en bekreftelse på e-post så snart betalingen er registrert.
        </p>
        {referenceId && (
          <p className="mt-3 text-sm text-muted-foreground font-body">
            Referanse: <span className="font-mono">{referenceId}</span>
          </p>
        )}

        <div className="mt-8 flex items-center justify-center gap-2 text-primary">
          <Heart className="h-5 w-5" />
          <span className="font-body font-semibold">
            Hele overskuddet går til syke barn
          </span>
        </div>

        <div className="mt-10 rounded-2xl border border-border bg-secondary/30 p-6 text-left">
          <h2 className="font-heading text-lg font-bold">Hva skjer videre?</h2>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground font-body">
            <li>1. Du får en kvittering på e-post.</li>
            <li>2. Vi pakker bestillingen og sender den med Posten — eller klargjør for henting hvis du valgte det.</li>
            <li>3. Du får beskjed når pakken er på vei.</li>
          </ul>
        </div>

        <div className="mt-6 text-left">
          <ProductImpactSection variant="confirmation" />
        </div>

        <Link
          to="/"
          className="mt-8 inline-flex rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground font-body shadow-lg transition-all hover:opacity-90"
        >
          Tilbake til forsiden
        </Link>
      </section>
    </Layout>
  );
};

export default OrderConfirmation;
